import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import LanguageSwitcher from "./LanguageSwitcher";
import SignInModal from "./SignInModal";
import { ArrowIcon } from "./ArrowIcon";

/**
 * Fixed top nav with responsive hamburger drawer (≤900px).
 * Desktop: horizontal links + sign-in + language switcher.
 * Mobile : hamburger button →︎ right-anchored sheet with stacked links.
 *
 * Phase 3 shell consolidation — the marketing nav only renders on
 * UNAUTHENTICATED surfaces (ChromeGate hides it on every portal / wizard
 * / admin path), so the nav no longer polls `/api/employer/session` to
 * flip to "Sign out". Authenticated sign-out lives in PortalChrome.
 */
export default function Nav() {
    const location = useLocation();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [signInOpen, setSignInOpen] = useState(false);

    // Close the drawer + modal on route change.
    useEffect(() => {
        setDrawerOpen(false);
        setSignInOpen(false);
    }, [location.pathname]);

    // Lock body scroll while drawer is open.
    useEffect(() => {
        if (!drawerOpen) return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = prev;
        };
    }, [drawerOpen]);

    // Escape closes drawer.
    useEffect(() => {
        if (!drawerOpen) return;
        const onKey = (e) => {
            if (e.key === "Escape") setDrawerOpen(false);
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [drawerOpen]);

    const go = (path) => (e) => {
        e.preventDefault();
        navigate(path);
    };

    const linkStyle = ({ isActive }) => ({
        borderBottomColor: isActive ? "var(--ink)" : undefined,
    });

    const openSignInFromDrawer = () => {
        setDrawerOpen(false);
        setSignInOpen(true);
    };

    const authButtonLabel = t("nav.signIn");
    const authButtonTestId = "nav-signin";
    const authButtonAction = () => setSignInOpen(true);

    return (
        <>
            <nav className="lg-nav" data-testid="lg-nav" aria-label={t("nav.aria")}>
                <Link
                    to="/"
                    className="logo"
                    data-testid="nav-logo"
                    onClick={go("/")}
                    style={{ textDecoration: "none", color: "inherit" }}
                >
                    Laguna<span>.</span>
                </Link>

                {/* Desktop nav — hidden below 900px */}
                <div className="nav-right desktop-only">
                    <NavLink
                        to="/employers"
                        className="nav-link"
                        data-testid="nav-employers"
                        style={linkStyle}
                    >
                        {t("nav.employers")}
                    </NavLink>
                    <NavLink
                        to="/how-it-works"
                        className="nav-link"
                        data-testid="nav-how-it-works"
                        style={linkStyle}
                    >
                        {t("nav.howItWorks")}
                    </NavLink>
                    <NavLink
                        to="/employees"
                        className="nav-link"
                        data-testid="nav-employees"
                        style={linkStyle}
                    >
                        {t("nav.employees")}
                    </NavLink>
                    <button
                        type="button"
                        className="nav-link"
                        data-testid={authButtonTestId}
                        onClick={authButtonAction}
                        style={{ color: "var(--accent)" }}
                        aria-label={authButtonLabel}
                    >
                        {authButtonLabel}
                        <ArrowIcon direction="up-right" className="arrow-nav-inline" />
                    </button>
                    <LanguageSwitcher />
                </div>

                {/* Mobile right cluster — language switcher + hamburger.
                 *  Language switcher is visible at all times on mobile (no longer
                 *  hidden inside the drawer) so locale switching is a single tap. */}
                <div className="nav-mobile-right mobile-only" data-testid="nav-mobile-right">
                    <div className="nav-mobile-lang"><LanguageSwitcher /></div>
                    <button
                        type="button"
                        className="nav-burger"
                        data-testid="nav-burger"
                        aria-label={drawerOpen ? "Close menu" : "Open menu"}
                        aria-expanded={drawerOpen}
                        aria-controls="nav-drawer"
                        onClick={() => setDrawerOpen((v) => !v)}
                    >
                        <span className={`burger-bar ${drawerOpen ? "is-open" : ""}`} />
                        <span className={`burger-bar ${drawerOpen ? "is-open" : ""}`} />
                        <span className={`burger-bar ${drawerOpen ? "is-open" : ""}`} />
                    </button>
                </div>
            </nav>

            {/* Mobile drawer */}
            <div
                className={`nav-drawer-scrim ${drawerOpen ? "is-open" : ""}`}
                data-testid="nav-drawer-scrim"
                onClick={() => setDrawerOpen(false)}
                aria-hidden={!drawerOpen}
            />
            <aside
                id="nav-drawer"
                className={`nav-drawer ${drawerOpen ? "is-open" : ""}`}
                data-testid="nav-drawer"
                aria-hidden={!drawerOpen}
                role="dialog"
                aria-label={t("nav.aria")}
            >
                <div className="nav-drawer-inner">
                    <NavLink
                        to="/employers"
                        className="drawer-link"
                        data-testid="drawer-employers"
                    >
                        {t("nav.employers")}
                    </NavLink>
                    <NavLink
                        to="/how-it-works"
                        className="drawer-link"
                        data-testid="drawer-how-it-works"
                    >
                        {t("nav.howItWorks")}
                    </NavLink>
                    <NavLink
                        to="/employees"
                        className="drawer-link"
                        data-testid="drawer-employees"
                    >
                        {t("nav.employees")}
                    </NavLink>
                    <button
                        type="button"
                        className="drawer-link drawer-signin"
                        data-testid="drawer-signin"
                        onClick={openSignInFromDrawer}
                    >
                        {authButtonLabel}
                        <ArrowIcon direction="up-right" className="arrow-nav-inline" />
                    </button>
                </div>
            </aside>
            <SignInModal open={signInOpen} onClose={() => setSignInOpen(false)} />
        </>
    );
}
